import {Box, Grid, InlineLink, PricingOptions, Tabs, Text} from '@primer/react-brand'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, INLINES} from '@contentful/rich-text-types'
import {InfoIcon, SparkleFillIcon} from '@primer/octicons-react'
import type {ComponentType, ReactElement, ReactNode} from 'react'

import type {PricingFeature, PricingPlan, PricingOption} from '../../../types'
import {collectFootnoteIndexMap, renderWithoutParagraphs} from '../../../utils'
import styles from './PricingSection.module.css'
import baseStyles from '../../../styles/base.module.css'

type PricingOptionsFeatureListProps = {
  children: ReactNode
  expanded?: boolean
}

const PricingOptionsFeatureList = PricingOptions.FeatureList as unknown as ComponentType<PricingOptionsFeatureListProps>

export function PricingSectionPlans({options}: {options: PricingOption[]}) {
  if (options.length === 0) return null
  const hasMultipleOptions = options.length > 1
  const footnoteIndexMap = collectFootnoteIndexMap(options)

  // When multiple pricing options exist (e.g. "For individuals" / "For businesses"),
  // render them as tabs so users can switch between plan groups
  if (hasMultipleOptions) {
    return (
      <Tabs aria-label="Pricing plans" variant="accent" className={styles.fullWidthTabsPanel}>
        {options.map(option => (
          <Tabs.Item key={option.sys.id}>{option.fields.label}</Tabs.Item>
        ))}
        {options.map(option => (
          <Tabs.Panel className={styles.tabs} key={option.sys.id}>
            <Box className={`${baseStyles.gridFrameTop} ${baseStyles.gridFrameBottom}`}>
              <Grid>
                <Grid.Column>{renderPlansForOption(option, footnoteIndexMap)}</Grid.Column>
              </Grid>
            </Box>
          </Tabs.Panel>
        ))}
      </Tabs>
    )
  }

  // Single pricing option: render plans directly without tabs
  const singleOption = options[0]
  if (!singleOption) return null

  return (
    <Box className={`${baseStyles.gridFrameTop} ${baseStyles.gridFrameBottom}`}>
      <Grid>
        <Grid.Column>{renderPlansForOption(singleOption, footnoteIndexMap)}</Grid.Column>
      </Grid>
    </Box>
  )
}

function renderPlansForOption(option: PricingOption, footnoteIndexMap: Map<string, number>) {
  const {plans} = option.fields
  const hasFeatures = plans.some(plan => (plan.fields.featureList?.length ?? 0) > 0)
  const variant = hasFeatures ? 'default' : 'default-gradient'

  return <PricingOptions variant={variant}>{plans.map(plan => renderPlanItem(plan, footnoteIndexMap))}</PricingOptions>
}

function renderPlanItem(plan: PricingPlan, footnoteIndexMap: Map<string, number>) {
  const {
    heading,
    description,
    price,
    originalPrice,
    priceTrailing,
    actionMessage,
    primaryCta,
    secondaryCta,
    featureListTitle,
    highlight,
    featureList,
    trailingText,
  } = plan.fields

  const featureListItems = featureList?.map(feature => renderFeature(feature, footnoteIndexMap)) ?? []
  const featureListChildren: ReactElement[] = [
    ...(featureListTitle
      ? [
          (
            <PricingOptions.FeatureListHeading key={`${plan.sys.id}-feature-list-heading`}>
              {featureListTitle}
            </PricingOptions.FeatureListHeading>
          ) as unknown as ReactElement,
        ]
      : []),
    ...featureListItems,
  ]

  return (
    <PricingOptions.Item key={plan.sys.id}>
      <PricingOptions.Heading>{renderWithoutParagraphs(heading)}</PricingOptions.Heading>
      {highlight && <PricingOptions.Label>{highlight}</PricingOptions.Label>}
      <PricingOptions.Description>{renderWithoutParagraphs(description)}</PricingOptions.Description>
      <PricingOptions.Price originalPrice={originalPrice} trailingText={priceTrailing}>
        {price}
      </PricingOptions.Price>
      {actionMessage && (
        <PricingOptions.ActionsMessage className={styles.alertMessage} leadingComponent={<InfoIcon />}>
          {documentToReactComponents(actionMessage, {
            renderNode: {
              [BLOCKS.PARAGRAPH]: (_, children) => <Text as="p">{children}</Text>,
              [INLINES.HYPERLINK]: (node, children) => <InlineLink href={node.data.uri}>{children}</InlineLink>,
            },
          })}
        </PricingOptions.ActionsMessage>
      )}
      {primaryCta && (
        <PricingOptions.PrimaryAction as="a" href={primaryCta.fields.href} variant="primary">
          {primaryCta.fields.text}
        </PricingOptions.PrimaryAction>
      )}
      {secondaryCta && (
        <PricingOptions.SecondaryAction as="a" href={secondaryCta.fields.href}>
          {secondaryCta.fields.text}
        </PricingOptions.SecondaryAction>
      )}
      {featureListChildren.length > 0 && (
        <PricingOptionsFeatureList expanded>{featureListChildren}</PricingOptionsFeatureList>
      )}
      {trailingText && (
        <PricingOptions.Footnote
          className={!featureList || featureList.length === 0 ? styles.trailingTextSansFeatures : ''}
        >
          {documentToReactComponents(trailingText, {
            renderNode: {
              [INLINES.HYPERLINK]: (node, children) => <InlineLink href={node.data.uri}>{children}</InlineLink>,

              /**
               * We render the paragraph as a fragment since PricingOptions.Footnote
               * already is a paragraph element (<p>) and we don't want to nest paragraphs
               * inside of each other.
               */
              [BLOCKS.PARAGRAPH]: (_, children) => <>{children}</>,
            },
          })}
        </PricingOptions.Footnote>
      )}
    </PricingOptions.Item>
  )
}

function renderFeature(feature: PricingFeature, footnoteIndexMap: Map<string, number>): ReactElement {
  const {title, text, variant, footnote} = feature.fields
  const resolvedVariant = variant ?? 'Included'

  if (resolvedVariant === 'Title') {
    return (
      <PricingOptions.FeatureListGroupHeading key={feature.sys.id}>
        {title ?? renderWithoutParagraphs(text)}
      </PricingOptions.FeatureListGroupHeading>
    )
  }

  const footnoteIndex = footnote ? (footnoteIndexMap.get(feature.sys.id) ?? -1) : -1

  const featureContent = (
    <>
      {renderWithoutParagraphs(text)}
      {footnoteIndex >= 0 && (
        <sup>
          <InlineLink href={`#footnote-${footnoteIndex + 1}`} aria-label={`Footnote ${footnoteIndex + 1}`}>
            {footnoteIndex + 1}
          </InlineLink>
        </sup>
      )}
    </>
  )

  if (resolvedVariant === 'Excluded') {
    return (
      <PricingOptions.FeatureListItem key={feature.sys.id} variant="excluded">
        {featureContent}
      </PricingOptions.FeatureListItem>
    )
  }

  if (resolvedVariant === 'Spark') {
    return (
      <PricingOptions.FeatureListItem
        key={feature.sys.id}
        leadingVisual={SparkleFillIcon}
        leadingVisualFill="var(--base-color-scale-purple-3)"
      >
        {featureContent}
      </PricingOptions.FeatureListItem>
    )
  }

  return <PricingOptions.FeatureListItem key={feature.sys.id}>{featureContent}</PricingOptions.FeatureListItem>
}
